import { useEffect, useRef, useState, type CSSProperties } from 'react'
import type { TouchEvent } from 'react'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import { FaMicrophone } from 'react-icons/fa'
import { downloadBevVcard, BEV_CONTACT } from './onboarding/bevContact'
import { AudioCaptureWrapper } from './AudioCaptureWrapper'
import OnboardingResults from './onboarding/OnboardingResults'

import Popup from './Popup'

const ONBOARDING_STEPS = [
  'Welcome to myScribbl',
  "",
  'Save Bev in your contacts',
  'Make your first call',
  'Here is what Bev captured',
  'How Scribbl works',
]

type OnboardingPopupProps = {
  open: boolean
  onClose: () => void
  onComplete: () => void
  userId: string | null
}

type CallControlsProps = {
  isRecording: boolean
  isLoading: boolean
  startRecording: () => void
  stopRecording: () => void
  onEnd: () => void
}

function CallControls({ isRecording, isLoading, startRecording, stopRecording, onEnd }: CallControlsProps) {
  const startedRef = useRef(false)

  useEffect(() => {
    if (!startedRef.current && !isRecording && !isLoading) {
      startedRef.current = true
      startRecording()
    }
  }, [isRecording, isLoading, startRecording])

  return (
    <button
      type="button"
      className="onboarding-btn danger"
      onClick={() => {
        stopRecording()
        onEnd()
      }}
      disabled={isLoading}
      style={{ marginTop: '135px' }}
    >
      End Call
    </button>
  )
}

function OnboardingPopup({ open, onClose, onComplete, userId }: OnboardingPopupProps) {
  const [onboardingStep, setOnboardingStep] = useState(0)
  const [isClosing, setIsClosing] = useState(false)
  const [revealOrigin, setRevealOrigin] = useState<{ x: number; y: number } | null>(null)
  const [contactAdded, setContactAdded] = useState(false)
  const [callStartTime, setCallStartTime] = useState<number>(0)
  const blockRef = useRef<HTMLButtonElement | null>(null)
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)
  const touchDeltaRef = useRef<{ x: number; y: number } | null>(null)

  useEffect(() => {
    if (onboardingStep === 3) {
      setCallStartTime(Date.now())
    }
  }, [onboardingStep])

  useEffect(() => {
    if (open) {
      setOnboardingStep(0)
      setIsClosing(false)
      setRevealOrigin(null)
      setContactAdded(false)
    }
  }, [open])

  const maxStepIndex = ONBOARDING_STEPS.length - 1
  const isLastStep = onboardingStep >= maxStepIndex
  const popupTitle = ONBOARDING_STEPS[onboardingStep] ?? 'Welcome'

  const handleNextStep = () => {
    setOnboardingStep((prev) => {
      if (prev === 2 && !contactAdded) return prev
      return Math.min(prev + 1, maxStepIndex)
    })
  }

  const handlePrevStep = () => {
    setOnboardingStep((prev) => Math.max(prev - 1, 0))
  }

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    if (isClosing) return
    const touch = event.touches[0]
    if (!touch) return
    touchStartRef.current = { x: touch.clientX, y: touch.clientY }
    touchDeltaRef.current = { x: 0, y: 0 }
  }

  const handleTouchMove = (event: TouchEvent<HTMLDivElement>) => {
    if (!touchStartRef.current) return
    const touch = event.touches[0]
    if (!touch) return
    touchDeltaRef.current = {
      x: touch.clientX - touchStartRef.current.x,
      y: touch.clientY - touchStartRef.current.y,
    }
  }

  const handleTouchEnd = () => {
    const delta = touchDeltaRef.current
    touchStartRef.current = null
    touchDeltaRef.current = null
    if (!delta || isClosing) return
    if (Math.abs(delta.x) < 45 || Math.abs(delta.x) < Math.abs(delta.y) + 10) return
    if (delta.x < 0 && onboardingStep < maxStepIndex) {
      handleNextStep()
    } else if (delta.x > 0 && onboardingStep > 0) {
      handlePrevStep()
    }
  }

  const handleFinalClose = () => {
    if (!isLastStep || isClosing) return
    const rect = blockRef.current?.getBoundingClientRect()
    if (rect) {
      setRevealOrigin({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 })
    } else {
      setRevealOrigin({ x: window.innerWidth / 2, y: window.innerHeight / 2 })
    }
    setIsClosing(true)
    window.setTimeout(() => {
      onComplete()
    }, 1000)
  }

  const handleDownloadBevVcard = () => {
    setContactAdded(true)
    downloadBevVcard()
  }

  const handleCallEnd = () => {
    setOnboardingStep(4) // Move to results slide
  }

  return (
    <>
      {isClosing && <div className="popup-blur-out" aria-hidden="true" />}
      {isClosing && (
        <div
          className="popup-screen-reveal"
          style={
            revealOrigin
              ? ({
                  ['--reveal-x' as string]: `${revealOrigin.x}px`,
                  ['--reveal-y' as string]: `${revealOrigin.y}px`,
                } as CSSProperties)
              : undefined
          }
          aria-hidden="true"
        />
      )}
      <Popup
        open={open}
        title={popupTitle}
        onClose={onClose}
        showClose={false}
        closeOnBackdrop={false}
        coverBackground={true}
        windowClassName="onboarding-popup-window"
      >
        <div
          className={`popup-flow${isClosing ? ' is-closing' : ''}`}
          data-step={onboardingStep}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <button
            type="button"
            className="popup-flow-block"
            onClick={isLastStep ? handleFinalClose : undefined}
            aria-label={isLastStep ? 'Finish' : undefined}
            tabIndex={isLastStep ? 0 : -1}
            ref={blockRef}
            style={{ 
              pointerEvents: isLastStep ? 'auto' : 'none',
              cursor: isLastStep ? 'pointer' : 'default',
              zIndex: isLastStep ? 10 : 5
            }}
          >
            {onboardingStep === 5 ? (
              <span className="popup-flow-block-label popup-flow-block-label-finish">Finish</span>
            ) : null}
          </button>
          <div className="popup-flow-content" style={{ zIndex: 6 }}>
            {onboardingStep === 0 && (
              <div className="popup-welcome popup-step-animated" key="step-welcome">
                <div className="popup-welcome-visual" aria-hidden="true">
                  <span className="popup-welcome-ring" />
                  <span className="popup-welcome-ring alt" />
                </div>
                <p className="popup-step-thanks">Thanks for signing up!</p>
                <p className="popup-step-text">
                  Your assistant is ready to capture calls, summaries, and follow-ups.
                </p>
                <div className="popup-swipe-hint" aria-hidden="true">
                  <FiChevronRight size={20} className="swipe-arrow" />
                  <span>Swipe to start</span>
                </div>
              </div>
            )}
            {onboardingStep === 1 && (
              <div className="popup-hero popup-step-animated" key="step-setup">
                <h3 className="popup-hero-title">Hey, I am Bev, let&apos;s get you started.</h3>
              </div>
            )}
            {onboardingStep === 2 && (
              <div className="popup-step popup-step-animated" key="step-contact">
                <br />
                <div className="popup-contact-card">
                  <div className="popup-contact-row">
                    <div className="popup-contact-avatar">B</div>
                    <div className="popup-contact-info">
                      <div className="popup-contact-name">{BEV_CONTACT.firstName}</div>
                      <div className="popup-contact-role">
                        {BEV_CONTACT.title}
                      </div>
                    </div>
                  </div>
                  <div className="popup-contact-meta">
                    <span className="popup-contact-label">Phone</span>
                    <strong className="popup-contact-value">{BEV_CONTACT.phoneNumber}</strong>
                  </div>
                </div>
                <button
                  type="button"
                  className={`popup-download-button${contactAdded ? '' : ' pulse'}`}
                  onClick={handleDownloadBevVcard}
                  style={{ background: '#f97316' }}
                >
                  {contactAdded ? 'Contact Added' : 'Add contact'}
                </button>
              </div>
            )}
            {onboardingStep === 3 && (
              <div className="popup-step popup-step-animated popup-step-call" key="step-first-call" style={{ alignItems: 'center', justifyContent: 'center', paddingTop: '60px' }}>
                <AudioCaptureWrapper
                  endpoint="/listen?mode=onboarding"
                  instanceId="onboarding"
                  userId={userId || undefined}
                >
                  {(controls) => (
                    <CallControls
                      isRecording={controls.isRecording}
                      isLoading={controls.isLoading}
                      startRecording={controls.startRecording}
                      stopRecording={controls.stopRecording}
                      onEnd={handleCallEnd}
                    />
                  )}
                </AudioCaptureWrapper>
              </div>
            )}
            {onboardingStep === 4 && (
              <div className="popup-step popup-step-animated" key="step-results" style={{ alignItems: 'center', justifyContent: 'flex-start', paddingTop: '10px' }}>
                <OnboardingResults 
                  userId={userId} 
                  callStartTime={callStartTime} 
                  onComplete={() => setOnboardingStep(5)}
                  isEmbedded={true}
                />
              </div>
            )}
            {onboardingStep === 5 && (
              <div className="popup-step popup-step-animated" key="step-frontend" style={{ alignItems: 'center', justifyContent: 'center', minHeight: '220px' }}>
                <p className="popup-step-text" style={{ fontSize: '14px' }}>
                  This dashboard keeps everything in one place: call notes, contacts, and your calendar.
                </p>
                <div className="popup-step-highlight">
                  Call Bev to capture anything you need! You can either call her directly or click on the <FaMicrophone /> button.
                </div>
                <p className="popup-step-hint">Click Finish to start using myScribbl.</p>
              </div>
            )}
          </div>
          <div className="popup-dots" aria-hidden="true" style={{ zIndex: 7 }}>
            {ONBOARDING_STEPS.map((_, index) => (
              <span
                key={index}
                className={`popup-dot${index === onboardingStep ? ' active' : ''}`}
              />
            ))}
          </div>
          <div className="popup-nav" style={{ zIndex: 7 }}>
            {onboardingStep > 0 && (
              <button
                type="button"
                className="popup-nav-button back"
                onClick={handlePrevStep}
                aria-label="Back"
                disabled={isClosing}
              >
                <FiChevronLeft aria-hidden size={18} />
              </button>
            )}
            {!isLastStep && (
              <button
                className="popup-nav-button primary"
                onClick={handleNextStep}
                aria-label="Next"
                disabled={isClosing || (onboardingStep === 2 && !contactAdded)}
              >
                <FiChevronRight aria-hidden size={18} />
              </button>
            )}
          </div>
        </div>
      </Popup>
    </>
  )
}

export default OnboardingPopup
